import { $post } from '@/api'
import {
    IMember,
    IMemberGroupListItem,
    IMemberLevelDetail,
    IMemberListParams,
    IMemberListResponse,
} from '@/model/types/member'
import { IShopConfig, IShopInfo } from '@/model/types/shop'

export const getMemberList = (params: IMemberListParams) =>
    $post<IMemberListResponse, IMemberListParams>({
        url: '/membership/getmemberlist',
        params,
    })

export const getMemberDetail = (memberId: number) =>
    $post<IMember, { member_id: number }>({
        url: '/membership/getmemberdetail',
        params: { member_id: memberId },
    })

export const getMemberGroupList = () =>
    $post<IMemberGroupListItem[], undefined>({
        url: '/membership/getmembergrouplist',
    })

export const changeMembershipGroup = (params: { membership_id_list: number[]; group_id: number }) =>
    $post<void, { membership_id_list: number[]; group_id: number }>({
        url: '/membership/changemembershipgroup',
        params,
    })

export const getMemberLevelList = () =>
    $post<IMemberLevelDetail[], undefined>({
        url: '/membership/getmemberlevellist',
    })

export const deleteMember = (memberId: number) =>
    $post<void, { member_id: number }>({
        url: '/membership/deletemember',
        params: { member_id: memberId },
    })

export const addMemberManual = (params: { real_name: string; phone: string; group_id?: number; card_code?: string }) =>
    $post<void, typeof params>({
        url: '/membership/addmembermanual',
        params,
    })

export const editMember = (params: {
    member_id: number
    name?: string
    phone?: string
    birthday?: string
    group_id?: number
    card_code?: string
}) =>
    $post<void, typeof params>({
        url: '/membership/editmember',
        params,
    })

export const editMemberPoint = (params: { member_id: number; change_points: number; remark?: string }) =>
    $post<void, typeof params>({
        url: '/membership/editmemberpoint',
        params,
    })

export const resetMemberLevel = (params: { member_id: number; new_level: number }) =>
    $post<void, typeof params>({
        url: '/membership/resetmemberlevel',
        params,
    })

export const rechargeMember = (params: {
    member_id: number
    serial_number: string
    amount: number
    bonus_amount: number
    activity_level_id: number
    remark?: string
    send_sms_to_customer: boolean
    payments: Array<{
        payment: number
        amount: number
    }>
}) =>
    $post<void, typeof params>({
        url: '/order/memberrecharge',
        params,
    })

export const getMemberConsumeRecords = (params: { member_id: number; page: number; limit: number }) =>
    $post<any, typeof params>({
        url: '/membership/memberconsumerecords',
        params,
    })

export const getBalanceChangeRecords = (params: {
    member_id: number
    page: number
    limit: number
    record_type_list?: number[]
}) =>
    $post<any, typeof params>({
        url: '/membership/getbalancechange',
        params,
    })

export const getMemberPointsRecords = (params: {
    member_id: number
    page: number
    limit: number
    change_type?: number[]
}) =>
    $post<any, typeof params>({
        url: '/membership/memberpointsrecords',
        params,
    })

export const getSvipPurchaseOrders = (params: {
    member_id?: number
    page: number
    limit: number
    keyword?: string
    from_date?: string
    to_date?: string
    item_types?: number[]
}) =>
    $post<any, typeof params>({
        url: '/membership/getsvippurchaseorder',
        params,
    })

export const createSvipEntitlementRefund = (params: { source_order_id: number; refund_reason?: string }) =>
    $post<any, typeof params & { entitlement_type: 'svip'; refund_method: 'original' }>({
        url: '/order/createentitlementrefund',
        params: {
            ...params,
            entitlement_type: 'svip',
            refund_method: 'original',
        },
    })

export const getMemberChainCoinRecords = (params: {
    member_id: number
    page: number
    limit: number
    change_type?: number[]
}) =>
    $post<any, typeof params>({
        url: '/membership/memberchaincoinrecords',
        params,
    })

export const getMemberRechargeRecords = (params: {
    page: number
    limit: number
    keyword?: string
    start_date?: string
    end_date?: string
    payment_list?: number[]
    custom_payment_ids?: number[]
}) =>
    $post<any, typeof params>({
        url: '/membership/memberrechargerecords',
        params,
    })

export const getConsumedRechargePayments = (params: { start_date: string; end_date: string; order_type: 1 }) =>
    $post<Array<{ payment_type: number; payment_name: string; custom_payment_id: number | null }>, typeof params>({
        url: '/shop/getconsumedpaymentconfig',
        params,
    })

export const getShopMemberCardAdmin = () =>
    $post<{ acode_url: string }, undefined>({
        url: '/membership/getshopmembercardadmin',
    })

export const getShopInfo = () =>
    $post<IShopInfo, undefined>({
        url: '/shop/getshopinfo',
    })

export const getShopConfig = () =>
    $post<IShopConfig, undefined>({
        url: '/shop/getshopconfig',
    })

export const updateShopConfig = (params: Partial<IShopConfig>) =>
    $post<void, Partial<IShopConfig>>({
        url: '/shop/updateshopconfig',
        params,
    })

export const getShopPayment = () =>
    $post<
        {
            paymentconfig?: {
                payment_list?: number[]
            }
            custom_payment_config?: Array<{ id: number; name: string; status?: number }>
        },
        undefined
    >({
        url: '/shop/getshoppayment',
    })

export const refundMemberBalance = (params: {
    member_id: number
    recharge_order_id?: number
    refund_amount: number
    refund_bonus_amount: number
    refund_type: number
    refund_reason?: string
    custom_payment_id?: number
}) =>
    $post<void, typeof params>({
        url: '/order/refundmemberbalance',
        params,
    })

export const updateMemberConfig = (params: {
    card_face_theme?: number
    app_recharge?: number
    member_pay_password_enabled?: boolean
    contact_method?: number[]
    phone?: string
    wx_qr_code_img?: string
    shop_logo_img?: string
    miniapp_blacklist_level?: number
}) =>
    $post<void, typeof params>({
        url: '/shop/updatememberconfig',
        params,
    })

export const getExperienceRules = () =>
    $post<Array<{ id: number; type: number; status: number | boolean; multiple: number }>, undefined>({
        url: '/membership/getexperiencerule',
    })

export const updateExperienceRules = (params: {
    rules: Array<{ type: number; status: number | boolean; multiple: number }>
}) =>
    $post<void, typeof params>({
        url: '/membership/updateexperiencerule',
        params,
    })

export const getMemberLevelChangeRecords = (params: { page: number; limit: number; keyword?: string }) =>
    $post<any, typeof params>({
        url: '/membership/getmemberlevelchangerecord',
        params,
    })

export const getBlacklistMembers = (params: { page: number; limit: number; keyword?: string }) =>
    $post<any, typeof params>({
        url: '/membership/listblacklistmembersminiapp',
        params,
    })

export const getBlacklistRecords = (params: { page: number; limit: number; keyword?: string }) =>
    $post<any, typeof params>({
        url: '/membership/listminiappblacklistrecords',
        params,
    })

export const blacklistMember = (params: { member_id: number; miniapp_blacklist_level?: number; remark?: string }) =>
    $post<void, typeof params>({
        url: '/membership/blacklistmemberminiapp',
        params,
    })

export const unblacklistMember = (params: { member_id: number }) =>
    $post<void, typeof params>({
        url: '/membership/unblacklistmemberminiapp',
        params,
    })

export const getNoPointGoods = (params: { page: number; limit: number }) =>
    $post<any, typeof params>({
        url: '/goods/getnopointgoodslist',
        params,
    })

export const getRealtimePointsList = (params: { page: number; limit: number }) =>
    $post<any, typeof params>({
        url: '/membership/getrealtimepointslist',
        params,
    })

export const getPointsDeductionList = (params: { page: number; limit: number; from_date?: string; to_date?: string }) =>
    $post<any, typeof params>({
        url: '/membership/getpointsdeductionlist',
        params,
    })

export const getPointsTotalList = (params: { page: number; limit: number }) =>
    $post<any, typeof params>({
        url: '/membership/getpointstotallist',
        params,
    })

export const getPointsDeductionRecords = (params: {
    page: number
    limit: number
    member_ids?: number[]
    from_date?: string
    to_date?: string
}) =>
    $post<any, typeof params>({
        url: '/membership/memberpointsdeductionrecord',
        params,
    })

export const getRealtimeBalanceList = (params: { page: number; limit: number; desc?: boolean }) =>
    $post<any, typeof params>({
        url: '/membership/getrealtimebalancelist',
        params,
    })

export const getMemberRechargeRankList = (params: {
    page: number
    limit: number
    from_date?: string
    to_date?: string
    scenes?: number[]
}) =>
    $post<any, typeof params>({
        url: '/membership/getmemberrechargelist',
        params,
    })

export const getMemberBalanceConsumeRankList = (params: {
    page: number
    limit: number
    from_date?: string
    to_date?: string
    scenes?: number[]
}) =>
    $post<any, typeof params>({
        url: '/membership/getmemberconsumelist',
        params,
    })

export const getPointsCouponMallConfig = () => $post<any, undefined>({ url: '/coupon/getpointscouponmallconfig' })

export const updatePointsCouponMallConfig = (params: { enabled: boolean }) =>
    $post<void, typeof params>({ url: '/coupon/editpointscouponmallconfig', params })

export const getPointsCouponItems = () => $post<any, undefined>({ url: '/coupon/listpointscouponexchangeitems' })

export const getPointsCouponCandidates = () => $post<any, undefined>({ url: '/coupon/getpointscouponcandidatecoupons' })

export const createPointsCouponItem = (params: { coupon_id: number; required_points: number; enabled: boolean }) =>
    $post<void, typeof params>({ url: '/coupon/createpointscouponexchangeitem', params })

export const updatePointsCouponItem = (params: {
    item_id: number
    coupon_id?: number
    required_points?: number
    enabled?: boolean
}) => $post<void, typeof params>({ url: '/coupon/editpointscouponexchangeitem', params })

export const getPointsCouponRecords = (params: { page: number; limit: number; item_id?: number }) =>
    $post<any, typeof params>({ url: '/coupon/getpointscouponexchangerecords', params })

export const editMemberGroup = (params: { group_id?: number; name: string; description?: string; status?: number }) =>
    $post<void, typeof params>({
        url: '/membership/editmembergroup',
        params,
    })

export const getMemberBirthdayList = (params: { page: number; limit: number }) =>
    $post<any, typeof params>({
        url: '/membership/getmemberbirthdaylist',
        params,
    })

export const getMemberUserBindingRecords = (params: {
    page: number
    limit: number
    keyword?: string
    from_date?: string
    to_date?: string
}) =>
    $post<any, typeof params>({
        url: '/membership/getmemberuserbindingrecords',
        params,
    })

export const getMemberCardRecords = (params: {
    page: number
    limit: number
    from_date?: string
    to_date?: string
    scene_list?: number[]
    inviter_ids?: number[]
}) =>
    $post<any, typeof params>({
        url: '/membership/getmembercardrecords',
        params,
    })

export const updateMemberLevel = (params: {
    id: number
    name?: string
    experience_threshold?: number
    experience_deduction?: number
    expiration?: number
}) =>
    $post<void, typeof params>({
        url: '/membership/updatememberlevel',
        params,
    })

export const addMemberLevel = (params: {
    name: string
    experience_threshold: number
    experience_deduction?: number
    expiration: number
    level: number
    benefits?: number[]
    discount_rate?: number
    booster_multiplier?: number
}) =>
    $post<void, typeof params>({
        url: '/membership/addmemberlevel',
        params,
    })

export const removeMemberLevel = (params: { id: number; level?: number }) =>
    $post<void, typeof params>({
        url: '/membership/removememberlevel',
        params,
    })

export const updateMemberDiscount = (params: {
    enable?: boolean
    weekdays?: number[]
    monthdays?: number[]
    has_balance?: boolean
    scenes?: number[]
    discount_level?: {
        member_level_id?: number | null
        level?: number | null
        discount_rate?: number | null
    }
}) =>
    $post<void, typeof params>({
        url: '/activity/updatememberdiscount',
        params,
    })

export const searchLsShop = (params: { shop_code: string }) =>
    $post<any, typeof params>({
        url: '/membership/migration/searchlsshop',
        params,
    })

export const requestLsShopValidation = (params: { ls_shop_id: number }) =>
    $post<any, typeof params>({
        url: '/membership/migration/requestlsshopvalidation',
        params,
    })

export const validateLsShop = (params: { ls_shop_id: number; validation_code: string }) =>
    $post<{ success: boolean }, typeof params>({
        url: '/membership/migration/validatelsshop',
        params,
    })

export const getLsMembers = (params: { ls_shop_id: number; page: number }) =>
    $post<any, typeof params>({
        url: '/membership/migration/getlsmembers',
        params,
    })

export const importLsMembers = (params: { ls_shop_id: number; ls_shop_name: string }) =>
    $post<void, typeof params>({
        url: '/membership/migration/importlsmembers',
        params,
    })

export const importMemberSheet = (params: { sheet_url: string }) =>
    $post<void, typeof params>({
        url: '/membership/migration/importmembersheet',
        params,
    })

export const getLsMemberImportRecords = (params: { page: number; limit: number }) =>
    $post<any, typeof params>({
        url: '/membership/migration/getlsmemberimportrecords',
        params,
    })

export const getLsMembersImported = (params: { record_id: number; page: number; limit: number }) =>
    $post<any, typeof params>({
        url: '/membership/migration/getlsmembersimported',
        params,
    })

export const getLsMemberImportInfo = (params?: { record_id?: number }) =>
    $post<any, typeof params>({
        url: '/membership/migration/getlsmemberimportinfo',
        params,
    })

export const deduplicateImportedLsMembers = (params: {
    record_id: number
    accept_imported_data?: boolean
    deal_deduplicated_members?: Array<{ member_id: number; source: number }>
}) =>
    $post<void, typeof params>({
        url: '/membership/migration/deduplicateimportedlsmembers',
        params,
    })

export const getShopMembershipExportCount = () =>
    $post<any, undefined>({
        url: '/membership/getshopmembershipexportcount',
    })

export const getVerificationCode = (params: { phone: string; code_type: number }) =>
    $post<void, typeof params>({
        url: '/staff/getverificationcode',
        params,
    })

export const verifyShopMembershipExportAdminPhone = (params: { phone: string; code: string }) =>
    $post<void, typeof params>({
        url: '/membership/verifyshopmembershipexportadminphone',
        params,
    })

export const verifyShopMembershipExportLegalRepresentativeId = (params: { id_tail: string }) =>
    $post<void, typeof params>({
        url: '/membership/verifyshopmembershipexportlrid',
        params,
    })

export const getShopAuthenticationInfo = () =>
    $post<{ legal_representative_name: string }, undefined>({
        url: '/shop/getshopauthenticationinfo',
    })

export const exportShopMemberships = () =>
    $post<void, undefined>({
        url: '/membership/exportshopmemberships',
    })

export const getShopMembershipsSheetUrl = () =>
    $post<{ sheet_url: string }, undefined>({
        url: '/membership/getshopmembershipssheeturl',
    })

export const loadMemberSettingConfig = (url: string) =>
    $post<any, undefined>({
        url,
    })

export const saveMemberSettingConfig = (url: string, params: any) =>
    $post<void, any>({
        url,
        params,
    })
